import {DOCUMENT} from '@angular/common';
import {inject, Injectable} from '@angular/core';
import {SeoAnalytics} from '../../../server/seo-config.contract';
import {SEO_CONFIG} from './seo-config.token';

/** Допустимые форматы ID — defence от инъекции в inline-script (admin доверен, но дёшево). */
const GA4_RE = /^G-[A-Z0-9]+$/i;
const GTM_RE = /^GTM-[A-Z0-9]+$/i;

/**
 * Инъекция счётчиков аналитики (GA4 / GTM / Яндекс.Метрика) в `<head>` витрины из
 * `SEO_CONFIG.analytics` (ТЗ-2 §2.4). Вызывается ТОЛЬКО в браузере через
 * `afterNextRender` (см. AppComponent) — скриптам нужен `window`, в SSR-HTML их не
 * зашиваем (иначе дубль при гидрации). Идемпотентно: повторный `inject()` и
 * уже-вставленные теги (по id) — no-op. На showcase (CSR, TransferState пуст →
 * SEO_CONFIG=fallback) счётчиков нет — инжектим только на индексируемом cabinet-домене.
 */
@Injectable({providedIn: 'root'})
export class AnalyticsInjectorService {
  private readonly seoConfig = inject(SEO_CONFIG);
  private readonly doc = inject(DOCUMENT);
  private done = false;

  inject(): void {
    if (this.done) {
      return;
    }
    this.done = true;

    const a: SeoAnalytics | undefined = this.seoConfig.analytics;
    if (!a) {
      return;
    }
    if (a.gtmId && GTM_RE.test(a.gtmId)) {
      this.injectGtm(a.gtmId);
    }
    if (a.ga4Id && GA4_RE.test(a.ga4Id)) {
      this.injectGa4(a.ga4Id);
    }
    (a.yandexMetrika ?? []).forEach((raw) => {
      const id = Number(raw);
      if (Number.isInteger(id) && id > 0) {
        this.injectMetrika(id, a.yandexMetrikaEcommerce);
      }
    });
  }

  /** Стандартный GTM-сниппет: загрузчик в <head> + noscript-iframe первым в <body>. */
  private injectGtm(id: string): void {
    if (this.doc.getElementById('gtm-script')) {
      return;
    }
    const s = this.doc.createElement('script');
    s.id = 'gtm-script';
    s.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${id}');`;
    this.doc.head.appendChild(s);

    const ns = this.doc.createElement('noscript');
    const iframe = this.doc.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${id}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    ns.appendChild(iframe);
    this.doc.body.insertBefore(ns, this.doc.body.firstChild);
  }

  /** GA4 (gtag.js): async-загрузчик + инициализация config. */
  private injectGa4(id: string): void {
    if (this.doc.getElementById('ga4-script')) {
      return;
    }
    const loader = this.doc.createElement('script');
    loader.id = 'ga4-script';
    loader.async = true;
    loader.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    this.doc.head.appendChild(loader);

    const init = this.doc.createElement('script');
    init.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${id}');`;
    this.doc.head.appendChild(init);
  }

  /** Яндекс.Метрика: стандартный tag.js-сниппет, опционально e-commerce dataLayer. */
  private injectMetrika(id: number, ecommerce: boolean): void {
    const elId = `ym-script-${id}`;
    if (this.doc.getElementById(elId)) {
      return;
    }
    const opts = `{clickmap:true,trackLinks:true,accurateTrackBounce:true,webvisor:true${ecommerce ? ',ecommerce:"dataLayer"' : ''}}`;
    const s = this.doc.createElement('script');
    s.id = elId;
    s.text = `(function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};m[i].l=1*new Date();for(var j=0;j<document.scripts.length;j++){if(document.scripts[j].src===r){return;}}k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,k.src=r,a.parentNode.insertBefore(k,a)})(window,document,'script','https://mc.yandex.ru/metrika/tag.js','ym');ym(${id},'init',${opts});`;
    this.doc.head.appendChild(s);
  }
}
